<template>
  <div class="setting-content--section">
    <AddEditProductModal 
      v-if="showProductModal" 
      @close="closeProductModal"
      allow-group-creation
      :product="productToEdit"
      :initial-group-id="initialGroupId"
      @product-created="retrieveGroupsAndProducts"
      @product-updated="retrieveGroupsAndProducts"
    />
    <div class="d-flex mb-4 w-100 justify-content-end flex-wrap" style="gap: 1rem;">
      <GekkoButton
        data-analytics-click="Products: create top settings page"
        :text="$gettext('Add product')"
        icon="fa-solid fa-plus"
        @click="openProductModal(null)"
      />
      <CreateProductGroup data-analytics-click="Products: create group top settings page" :createdCallback="retrieveGroupsAndProducts" />
    </div>
    <div v-for="itemGroup in populatedGroups" :key="itemGroup.name" class="product-group-wrap">
      <div class="product-group-name">
        <h3>{{ itemGroup.name }}</h3>
        <div>
          <CreateProductGroup data-analytics-click="Products: edit group" :initialGroup="itemGroup" :createdCallback="retrieveGroupsAndProducts" />
          <GekkoButton 
            :disabled="itemGroup.id===null"
            icon="fa-solid fa-trash-can"
            class="text-danger p-0"
            variant="link"
            v-gk-confirm="{
              title: $gettext('Delete group'),
              message: $gettext('Are you sure you want to delete this product group?'),
              options: {
                variant: 'danger',
                confirmText: $gettext('Delete'),
                item: $gettext('Group') + ' - ' + itemGroup.name
              },
              callback: deleteGroup,
              callbackParams: itemGroup.id
            }"
          />
        </div>
      </div>
      <div class="product-list-group-table-wrap">
        <b-table
          :items="itemGroup.products"
          :class="['product-list-table gk-table gk-table--responsive gk-table--headed gk-table--striped gk-table--hover']"
          :fields="groupTableFields"
          @row-clicked="rowClicked"
        >
          <template #cell(description)="data">
            {{ data.item.description }}
          </template>
          <template #cell(amount)="data">
            €{{ data.item.amount | formatNumber }}
          </template>
          <template #cell(tax_rate)="data">
            <div v-if="!data.item.vat_exempt">{{ data.item.tax_rate }}%</div>
            <div v-if="data.item.vat_exempt" v-translate> VAT Exempt </div>
          </template>
          <template #cell(total_inc_vat)="data">
            €{{ data.item.total_inc_vat | formatNumber }}
          </template>
          <template #cell(status)="data">
            <ProductArchive :data_object="data.item"></ProductArchive>
          </template>
          <template #cell(actions)="data">
            <b-dropdown variant="link" right toggle-class="text-decoration-none" no-caret :disabled="data.item.archived" class="ui-dashboard-menu-dropdown">
            <template v-slot:button-content>
              <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" class="m-0" />
            </template>
              <b-dropdown-item @click="rowClicked(data.item)">
                <font-awesome-icon icon="fa-solid fa-pen-to-square" /> {{ $gettext('Edit') }}
              </b-dropdown-item>
              <b-dropdown-item>
              <GekkoButton 
                icon="fa-solid fa-trash-can"
                class="text-danger p-0"
                variant="link"
                :text="$gettext('Delete product')"
                size="sm"
                v-gk-confirm="{
                  title: $gettext('Delete product'),
                  message: $gettext('Are you sure you want to delete this product?'),
                  options: {
                    variant: 'danger',
                    confirmText: $gettext('Delete'),
                    item: $gettext('Product') + ' - ' + data.item.description
                  },
                  callback: deleteProduct,
                  callbackParams: data.item
                }"
              />
            </b-dropdown-item>
          </b-dropdown>
          </template>
        </b-table>
      </div>
      <GekkoButton
        data-analytics-click="Products: create"
        :text="$gettext('Add product to') + ' ' + itemGroup.name"
        icon="fa-solid fa-plus"
        variant="link"
        class="text-info"
        @click="openProductModal(itemGroup.id)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import ProductService from "@/store/services/product.service.js"
import ProductGroupService from "@/store/services/productgroup.service.js"
import CreateProductGroup from "@/components/products/CreateProductGroup"
import ProductArchive from "@/components/products/ProductArchive"
import GekkoButton from "@/components/GekkoButton"

import AddEditProductModal from "@/components/products/AddEditProductModal"


// mixins
import settingsPageSetup from "@/mixins/settingsPageSetup"

export default {
  name: "ProductList",
  components: {
    CreateProductGroup,
    ProductArchive,
    GekkoButton,
    AddEditProductModal
  },
  mixins: [settingsPageSetup],
  data () {
    return {
      pageSettings: {
        title: this.$gettext("My products"),
        showCompanyPicker: true
      },
      groupTableFields: [
        { key:'description', label: this.$gettext("Description"), tdClass: 'col-description coldesc' },
        { key:'amount', label: this.$gettext("Amount ex. vat"), tdClass: 'tablecol-xs text-right', thClass: 'text-right' },
        { key:'tax_rate', label: this.$gettext("Tax rate"), tdClass: 'tablecol-xs text-right', thClass: 'text-right' },
        { key:'total_inc_vat', label: this.$gettext("Amount inc. vat"), tdClass: 'tablecol-xs text-right', thClass: 'text-right' },
        { key: 'status', label: '', tdClass: 'tablecol-sm text-right', thClass: 'text-right' },
        { key: 'actions', label: '', tdClass: 'col-avatar text-right', thClass: 'text-right' }
      ],
      items: [],
      groups: [],
      showProductModal: false,
      productToEdit: null,
      initialGroupId: null
    }
  },
  computed: {
    ...mapGetters({ 
      getCompanyId: 'main/getCompanyId'
    }),
    populatedGroups(){
      var chosen_groups = [];
      for(var i=0; i<this.groups.length; i++){
        if(this.groups[i].id !== null || (this.groups[i].products && this.groups[i].products.length>0)){ // remove the null check to hide all empty groups. 
          chosen_groups.push(this.groups[i]);
        }
      }
      return chosen_groups;
    }
  },
  created () {
    this.retrieveGroupsAndProducts()
  },
  methods: {
    openProductModal (group_id = null) {
      if (group_id) {
        this.initialGroupId = group_id
      }
      this.showProductModal = true
    },
    closeProductModal () {
      this.showProductModal = false
      this.productToEdit = null
      this.initialGroupId = null
    },
    rowClicked (item) {
      this.showProductModal = true
      this.productToEdit = item
    },
    retrieveGroupsAndProducts () {
      // Retrieve groups, then products.
      ProductGroupService.list({ company_profile: this.getCompanyId }).then((response) => {
        this.groups = response.data
        this.groups = this.groups.sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0))
        this.groups.unshift({ id: null, name: this.$gettext('No Group') })
        this.retrieveProducts()
      })
    },
    retrieveProducts () {
      ProductService.list({ company_profile: this.getCompanyId }).then((response) => {
        this.items = response.data.sort((a,b) => (a.group > b.group) ? 1 : ((b.group > a.group) ? -1 : 0))
        this.groups.forEach(group => {
          const group_products = this.items.filter(item => { return item.group === group.id })
          this.$set(group, 'products', group_products)
        })
      })
    },
    showGroup(groupData){
      console.log("AAAAAAAAAAAAAAAA");
      console.log(groupData.products && groupData.products.length > 0);
      return groupData.products && groupData.products.length > 0;
    },
    deleteGroup (pk) {
      ProductGroupService.destroy(pk).then(() => {
        if (window.mixpanel) {
          window.mixpanel.track('Product group: delete')
        }
        this.retrieveGroupsAndProducts()
      })
    },
    deleteProduct (item) {
      ProductService.destroy(item.id).then(() => {
        this.retrieveGroupsAndProducts()
      })
    }
  }
}
</script>
<style scoped>
  .mw-130{
    min-width: 130px;
  }
  .row-archived{
    color: gray;
  }
  .row-archived:hover{
    color: gray !important;
  }
</style>
<style lang="scss" scoped>
  .product-list-group-table-wrap {
  }
  .product-list-table {
    .col-description { font-size: 1rem; }
    .tablecol-sm {
      width: 150px;
    }
  }
  .product-group-wrap {
    margin-bottom: 2rem;
    .product-group-name {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      width: 100%;
      h3 { display: inline-block; }
    }
  }
</style>
